import {fetchQuery} from 'relay-runtime';
import DirectApi from '../../../directApi';
import environment from '../../../RelayEnvironment';
import Util from '../../../forecast-app/shared/util/util';
import FavoriteTaskMutation from '../../../mutations/favorite_task_mutation';
import AcquireReportLockMutation from '../../../mutations/reporting/AcquireReportLockMutation';
import DiscardReportDraftMutation from '../../../mutations/reporting/DiscardReportDraftMutation';
import EnterReportDraftMutation from '../../../mutations/reporting/EnterReportDraftMutation';
import HeartbeatReportLockMutation from '../../../mutations/reporting/HeartbeatReportLockMutation';
import PublishReportMutation from '../../../mutations/reporting/PublishReportMutation';
import ReleaseReportLockMutation from '../../../mutations/reporting/ReleaseReportLockMutation';
import SaveReportAsMutation from '../../../mutations/reporting/SaveReportAsMutation';
import UpdateSavedReportMutation from '../../../mutations/reporting/update_saved_report_mutation';
import {toRelayGlobalId} from '../../../containers/internal/nova-studio/shared/relayGlobalId';
import {Filter, ReportDefinitionV1, FieldNode as DefinitionFieldNode} from '../contracts/report-definition.types';
import {effectiveReportPeriod} from '../periodFilter';
import {ReportTaxonomy} from '../contracts/report-taxonomy.types';
import {groupingCellPaths, GroupingToken, isProducibleGroupingToken} from '../ReportTable/useClientGrouping';
import {grainRootOf, normalizeFieldTree} from './definitionFields';
import {ReportDataResult} from './reportRowModel';
import ReportLockStatusQuery from './ReportLockStatusQuery';
import {
	LoadedReport,
	LockClaim,
	LockStatus,
	ReportDataFetchError,
	ReportDataSource,
	ReportDraftState,
	ReportExportTooLargeError,
	ReportLifecycleState,
	ReportRefusalReason,
	ViewerPermission,
} from './ReportDataSource';
import {FieldNode, mapFetchResponse, RowMappingContext, rowMappingContextFor} from './reportRowMapper';
import {FormatLabel} from '../projectStatusLabels';
import {buildReportExportInputs, ReportExportInputs} from './reportExportInputs';
import {toReportDefinition} from './storedDefinition';
import {withoutValuelessConditions} from './outboundFilter';
import {
	buildSaveReportAsInput,
	buildUpdateSavedReportInput,
	SaveReportAsInputFields,
	SavedReportRef,
	UpdateSavedReportInputFields,
} from './savedReportInput';

/**
 * Real `ReportDataSource`. report-service is server-side only (the browser never calls it directly),
 * so this goes through the graphql Node server's thin authenticated passthrough express route
 * `POST /report/data/fetch` (see graphql `server.js` + `ReportServiceLogic.fetchTaskReportBuilderData`),
 * which forwards to report-service and returns the RAW hierarchical wrapped-node JSON that
 * `reportRowMapper` maps into `ReportRow`s.
 *
 * WHY an express passthrough and NOT a Relay field (the codebase's usual preference): the response is
 * a freeform, recursively-nested `{name,value}` blob defined by the report-service taxonomy, not by
 * our schema. Relay-modelling it would need a JSON scalar (loses all typing anyway) or a big
 * recursive type + fragments — real complexity for no benefit over the typed client-side mapper. The
 * route rides the same graphql-server host the browser already hits for `/report/*_export_csv`, so it
 * works in prod (ALB) with no new nginx/terraform routing. Auth is the standard credentialed
 * (cookie session) fetch — same as the export_csv callers.
 */
const FETCH_PATH = 'report/data/fetch';

/** Sibling passthrough returning a saved report's definition (see graphql `server.js`). The saved-report
 * Relay shape carries columns only as a flattened eye-options string, so re-opening a report reads the
 * definition directly rather than rebuilding the field tree in the client. */
const DEFINITION_PATH = 'report/definition';

/** Sibling passthrough returning the full (unpaginated) export as decoded CSV bytes (see graphql
 * `server.js` + report-service's ad-hoc export). Forwards the posted body verbatim to report-service,
 * so the body built here IS the backend contract. */
const EXPORT_PATH = 'report/data/export';

/** What report-service returns when an ordered export's result set exceeds its row ceiling. It fails
 * rather than truncating, because a short file that looks complete is a wrong answer the user cannot
 * detect — so this status means "narrow the report", never "try again". */
const EXPORT_TOO_LARGE_STATUS = 413;

/** The exact JSON body `EXPORT_PATH` forwards to report-service, named so the contract is checked
 * rather than inferred: an untyped object literal would let a mistyped `hierarchical` or a dropped
 * `orderBy` compile and only fail against the live backend. Extends `ReportExportInputs` so the
 * `{labels, dataProcessorInputs, orderBy}` half stays defined in one place. */
export interface ReportExportBody extends ReportExportInputs {
	filter: Filter;
	hierarchical: boolean;
}

/** Builds the export request body: `buildReportExportInputs`' `{labels, dataProcessorInputs, orderBy}`
 * plus the definition's canonical filter forwarded as-is, plus `hierarchical: true` — the
 * builder always wants the depth-first row order that lets a flat CSV reconstruct the task tree from
 * its id/parent-id columns (see `reportExportInputs.ts`). The caller passes the grain's catalog when
 * it has one, so the file's headings read like the table's; without it they fall back to the static
 * task labels and then the raw path, as `buildReportExportInputs` already handles. */
export function buildExportBody(
	definition: ReportDefinitionV1,
	grouping: GroupingToken[],
	hiddenColumnPaths?: readonly string[],
	taxonomy?: ReportTaxonomy | null
): ReportExportBody {
	const {labels, dataProcessorInputs, orderBy} = buildReportExportInputs(definition, grouping, taxonomy, hiddenColumnPaths);
	return {
		labels,
		dataProcessorInputs,
		orderBy,
		filter: withoutValuelessConditions(definition.filter),
		hierarchical: true,
	};
}

/** The field-tree root report-service resolves a project-status definition under. Only this grain
 * publishes `customFields.<Label>` grouping tokens, so a custom-field leaf is never added under any
 * other root. */
const PROJECT_STATUS_FIELD_ROOT = 'projectStatus';

/** Prefix a grouping token names a company project custom field with — see `fetchFieldsFor`. */
const CUSTOM_FIELD_GROUPING_PREFIX = 'customFields.';

/** The field-tree node name a custom field's leaf sits under, matching the grouping prefix. */
const CUSTOM_FIELD_TREE_NODE = 'customFields';

/** `ensureFieldPath` (`ReportTable/useReportTableColumns.tsx`), taking the path already split into
 * segments rather than a dotted string. Exists because a custom field's label is ONE path segment
 * that may itself contain a dot, and splitting a dotted string can never tell that dot apart from a
 * real segment boundary — only the caller, which knows where the label starts, can draw that line.
 *
 * Same contract as `ensureFieldPath`: pure (never mutates `fields`), and returns `fields` itself, by
 * reference, at every level where the full segment path is already present — the only way a caller
 * can tell "nothing to add" from "rebuilt to the same shape" without a deep-equality check. */
function ensureFieldSegments(fields: DefinitionFieldNode[], segments: string[]): DefinitionFieldNode[] {
	const [head, ...rest] = segments;
	const existing = fields.find(node => node.name === head);

	if (rest.length === 0) {
		return existing ? fields : [...fields, {name: head}];
	}

	const children = ensureFieldSegments(existing?.children ?? [], rest);
	if (existing && children === existing.children) {
		return fields;
	}

	const replacement: DefinitionFieldNode = {name: head, children};
	return existing ? fields.map(node => (node === existing ? replacement : node)) : [...fields, replacement];
}

/** The path, as segments, a grouping token's cell is fetched under, or null when there is nothing to
 * add for it.
 *
 * A custom-field token keeps its label as ONE path segment, never split on `.`: report-service builds
 * its taxonomy key by plain concatenation (`projectStatus.customFields.` + label), so a label
 * containing a dot must not invent a nesting level neither side has.
 *
 * A token grouping will drop adds nothing, so the fetch never asks for a field on behalf of a level
 * the table has already discarded: the same `isProducibleGroupingToken` decision `groupRows` makes.
 * The one exception is a custom-field token while the catalog has not loaded: its path needs no
 * catalog, and holding it back would leave a correctly saved custom-field grouping empty until the
 * catalog arrives and the fetch runs again. */
function fetchSegmentsFor(
	root: string,
	token: GroupingToken,
	cellPaths: Record<string, string>,
	taxonomy: ReportTaxonomy | null | undefined
): string[] | null {
	if (token.startsWith(CUSTOM_FIELD_GROUPING_PREFIX)) {
		const label = token.slice(CUSTOM_FIELD_GROUPING_PREFIX.length);
		if (root !== PROJECT_STATUS_FIELD_ROOT || !label) {
			return null;
		}
		if (taxonomy && !isProducibleGroupingToken(token, cellPaths, taxonomy)) {
			return null;
		}
		return [root, CUSTOM_FIELD_TREE_NODE, label];
	}
	if (!isProducibleGroupingToken(token, cellPaths, taxonomy)) {
		return null;
	}
	return cellPaths[token].split('.');
}

/** The field tree to send on the FETCH ONLY: `definition.fields`, augmented with the field every
 * active grouping token reads, wherever the definition does not already select it.
 *
 * Grouping is applied client-side over the fetched rows (`useClientGrouping`), and report-service
 * returns a sub-entity's name or a leaf only when the request names it - `ServiceCompanyEntityResolver`
 * fills a sprint or deliverable stub only for a requested alias, and `ProjectStatusResolver` drops
 * every unrequested node. So grouping by a field that is not also a column fetched nothing for it and
 * collapsed every row into one "No <field>" bucket: sprint, client and deliverable on the
 * task grain, labels and priority on project status, and a custom field. The other tokens only
 * worked because their fields happen to be default columns. (Deliverable matters twice: report-service
 * also nests tasks under their deliverable only when a `task.deliverable.*` leaf is selected.) Each
 * token's path is resolved by
 * `groupingCellPaths` - the same map grouping reads its cells through - so the two cannot disagree.
 *
 * Deliberately does NOT mutate `definition.fields`, and the result is used for the fetch body alone
 * — never written back into the definition. What the table renders as a column, what the Columns
 * dropdown offers, and what a save persists all come from `definition.fields` alone
 * (`flattenFieldPaths`), so augmenting only the outgoing request gets the missing data without
 * materialising a column the reader never asked to see. (The mapper keys a fetched-but-undisplayed
 * node under its own path - see `displayKeysFor` in `reportRowMapper` - which is the path grouping
 * reads.)
 *
 * Returns `definition.fields` itself, by reference, when nothing needs adding, so a report grouped
 * only by fields it already shows sends exactly the body it always did, and `fetchFieldsKey` does not
 * refetch for it. The one exception is a definition that arrives in the flat dotted-name shape and
 * needs a leaf: that is normalised into the contract's tree shape first (see the comment in the
 * loop), so what comes back is an equivalent tree rather than the same array.
 *
 * `taxonomy` decides where a project-status token's field lives (`<root>.<token>.name` or
 * `<root>.<token>`), so until it loads only a custom-field token is coupled there - its path needs no
 * catalog. The task grain's paths are fixed and need none either. */
export function fetchFieldsFor(
	definition: Pick<ReportDefinitionV1, 'fields'>,
	grouping: readonly GroupingToken[],
	taxonomy?: ReportTaxonomy | null
): DefinitionFieldNode[] {
	const root = grainRootOf(definition);
	const cellPaths = groupingCellPaths(root, taxonomy);
	// Annotated rather than inferred: `definition.fields` is a NON-EMPTY tuple
	// (`[FieldNode, ...FieldNode[]]`, from the contract's `minItems: 1`), and inferring that here
	// would refuse the rebuilt plain array `ensureFieldSegments` returns. Widening the local is
	// safe: adding a segment can only ever grow the tree, never empty it.
	let fields: DefinitionFieldNode[] = definition.fields;
	for (const token of grouping) {
		const segments = fetchSegmentsFor(root, token, cellPaths, taxonomy);
		if (!segments) {
			continue;
		}
		// A definition does not always arrive as the tree the contract specifies: the drafting agent
		// emits some with every column as one flat node whose name is the whole dotted path
		// (`{name:'task.project.info'}`). The root is then named by no node, so appending the leaf
		// below would add a SECOND root beside the flat ones - a tree naming the root twice, which is
		// neither shape report-service is sent today - and a flat definition that already selects this
		// very field would get it selected twice. Rewriting to the tree shape first (the same
		// normalisation the save path applies) gives the leaf one real root to merge into. Done lazily,
		// inside the branch that actually adds something, so every other definition still gets
		// `definition.fields` back untouched and by reference.
		if (fields === definition.fields && !fields.some(node => node.name === root)) {
			fields = normalizeFieldTree(fields);
		}
		fields = ensureFieldSegments(fields, segments);
	}
	return fields;
}

/** The row-mapping context for a fetch, built from the field tree that fetch actually SENT
 * (`fetchFieldsFor`) rather than from `definition.fields` alone.
 *
 * The mapper keys a repeated sub-entity (a project's owners, a task's labels) under whichever leaf
 * the request selected beneath it, falling back to the node's own path when none was selected. A
 * grouping-only leaf is selected by the fetch but not by the definition, so keying off the
 * definition would file an owner under `projectStatus.owner` while grouping by owner reads
 * `projectStatus.owner.name` - the value fetched, and then missed, so every row still fell into one
 * "No owner" group. Keying off the sent tree puts each cell exactly where grouping reads it. Nothing
 * else changes: the columns shown still come from the definition, and a leaf the definition already
 * selects is keyed the same either way. */
export function fetchMappingContext(
	definition: Pick<ReportDefinitionV1, 'fields'>,
	grouping: readonly GroupingToken[],
	taxonomy?: ReportTaxonomy | null,
	formatLabel?: FormatLabel
): RowMappingContext {
	// Never empty: `fetchFieldsFor` only ever grows `definition.fields`, which the contract requires
	// to carry at least one node.
	const fields = fetchFieldsFor(definition, grouping, taxonomy) as ReportDefinitionV1['fields'];
	return rowMappingContextFor({fields}, taxonomy, formatLabel);
}

/** Whether a bound is the zero-padded ISO calendar date report-service can bind to a `LocalDate`.
 * Shape only - a real-world impossible date like `2026-02-31` is left to report-service, which
 * names it in its own error rather than being second-guessed here. */
function isIsoDate(value: string | null): value is string {
	return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * The window the fetch reports against, as `startDate`/`endDate`, or nothing at all.
 *
 * This is NOT the filter. The filter conditions already decide which rows appear; these two say
 * which moment each row's time-dependent values describe - which retainer period a project-status
 * row reports, above all. Sending nothing makes report-service fall back to the period containing
 * today, which is how a report opened on October rendered a header naming October above September's
 * retainer figures: the range was shown to the reader, applied as a filter, and never reported
 * against.
 *
 * Sent on every grain, not just project status. A grain with no time-dependent values ignores the
 * window entirely (only `ProjectStatusResolver` reads it today), so gating on the grain here would
 * be a second place to remember the next one.
 *
 * Read through `effectiveReportPeriod`, the same helper the date control renders from, so the two
 * cannot answer differently for one report.
 *
 * A half-open window is sent as the one bound it has, not dropped. report-service reads a missing
 * side as open on that side and says so outright (`ReportDateRange`: "a report asking for everything
 * up to a date carries only an end"), and only a window with NEITHER bound falls back to the period
 * containing today. Withholding the one bound a reader did set would therefore not be cautious - it
 * would put exactly this bug back for every report scoped from a date, or up to one.
 *
 * A bound that is not ISO `yyyy-mm-dd` is dropped rather than sent, for the same reason an inverted
 * one is: report-service binds these two to a `LocalDate` and answers a value it cannot parse with a
 * 400 for the whole fetch. Only the definition's own `startDate`/`endDate` are shape-checked
 * upstream (the report-definition contract declares them `format: date`); the fallback source is not
 * - a period recovered from filter conditions is whatever string sits in a condition's `values`,
 * which the contract types as a bare string. That fallback exists precisely for definitions this
 * client did not write, so an unparseable date there would turn a report that renders today into a
 * hard error. Checked before the comparison below, which reads the two bounds as strings and is only
 * sound for the zero-padded ISO shape.
 *
 * An inverted window is dropped rather than sent. report-service rejects a SUPPLIED inverted range
 * with a 400 naming both dates, while it forgives one a saved report merely has STORED, reporting
 * today's period instead. A stored range this client has never been able to send is exactly the
 * legacy shape that could hold one, so sending it unchecked would turn reports that render today
 * into a hard error.
 */
function reportedWindow(definition: ReportDefinitionV1): {startDate?: string; endDate?: string} {
	const period = effectiveReportPeriod(definition);
	const startDate = isIsoDate(period.startDate) ? period.startDate : null;
	const endDate = isIsoDate(period.endDate) ? period.endDate : null;
	if (startDate !== null && endDate !== null && startDate > endDate) {
		return {};
	}
	return {
		...(startDate !== null ? {startDate} : {}),
		...(endDate !== null ? {endDate} : {}),
	};
}

/** Build the fetch request body from the report definition. `hierarchical: true` is REQUIRED to get the
 * nested / deliverable-tier / context-row response the builder table renders (false/null returns a
 * flat Standard-report array).
 *
 * The definition's `fields` decide the request tree, but are not forwarded completely verbatim: the
 * active `grouping` can name a field the definition does not itself select (see `fetchFieldsFor`),
 * and that leaf is added for the fetch alone. A report whose grouping needs nothing extra gets
 * `definition.fields` back unchanged by reference — no display-token expansion is otherwise needed
 * here. `taxonomy` is what places a project-status token's field; see `fetchFieldsFor`.
 *
 * `grouping` defaults to empty, so a caller that has none to report (or predates grouping-aware
 * fetching) gets exactly today's `definition.fields` verbatim.
 *
 * Duration columns need the project's estimation unit to render, but the client does NOT request it:
 * report-service guarantees `project.estimationUnit` (+ `minutesPerEstimationPoint`) travels with any
 * hierarchical response that carries a unit-bearing column, so it is always present for the mapper
 * without bloating the definition. */
export function buildFetchBody(
	definition: ReportDefinitionV1,
	page: {offset: number; limit: number},
	grouping: readonly GroupingToken[] = [],
	taxonomy?: ReportTaxonomy | null
): Record<string, unknown> {
	return {
		fields: fetchFieldsFor(definition, grouping, taxonomy),
		filter: withoutValuelessConditions(definition.filter),
		pagination: {offset: page.offset, limit: page.limit},
		hierarchical: true,
		...reportedWindow(definition),
	};
}

/** The `savedReport` node both saved-report mutations return, narrowed to what a save needs back. */
type SavedReportPayload = {id: string; privateAccess: boolean | null} | null | undefined;

/** Mutation response keyed by the mutation's root field (`saveReportAs` / `updateSavedReport`). */
type SavedReportMutationResponse = Record<string, {savedReport?: SavedReportPayload} | undefined>;

/** A saved-report mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type SavedReportMutationModule = {commit: unknown};

/** Runs a saved-report mutation as a promise so the caller can await the save and react to a
 * failure.
 *
 * The default `Util.CommitMutation` failure path pops a global mutation-error modal; the report
 * builder reports a failed save on the surface itself and keeps the unsaved draft editable, so the
 * `onError` override deliberately does NOT delegate to that fallback. It also gives up the
 * wrapper's one automatic retry of a transient network failure — the surface's error state invites
 * the user to press Save again instead.
 *
 * A response that resolves without a `savedReport` id is treated as a failure: without an id a
 * follow-up save could not target the report and would silently create a duplicate. */
function commitSave(
	mutation: SavedReportMutationModule,
	input: SaveReportAsInputFields | UpdateSavedReportInputFields,
	selectSavedReport: (response: SavedReportMutationResponse) => SavedReportPayload
): Promise<SavedReportRef> {
	return new Promise<SavedReportRef>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			input,
			(response: SavedReportMutationResponse) => {
				const savedReport = selectSavedReport(response);
				if (!savedReport?.id) {
					reject(new Error('saved report mutation returned no report'));
					return;
				}
				resolve({id: savedReport.id, privateAccess: savedReport.privateAccess ?? true});
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** The lifecycle payload each mutation returns, keyed by its root field. `draftState` arrives typed
 * as `ReportDraftState | null` by the generated Relay types, but the underlying GraphQL field is a
 * plain `String`, not an enum — report-service is free to add or misspell a value with no schema
 * change, so the raw payload is read as an unnarrowed string here and validated in `narrowDraftState`
 * before it reaches the union type. */
type LifecycleMutationResponse = Record<
	string,
	(Omit<ReportLifecycleState, 'draftState'> & {draftState?: string | null}) | undefined
>;

const KNOWN_DRAFT_STATES: ReadonlySet<string> = new Set(['PUBLISHED', 'DRAFT', 'UNPUBLISHED']);

/** Narrows a raw `draftState` string to the known union, mapping anything else — including a value
 * report-service might add later — to null rather than throwing. Consumers treat a null/unknown
 * state as "no reader isolation proven", the conservative reading: a publish that actually succeeded
 * must not be reported as a failure just because of an unrecognised state string. */
function narrowDraftState(draftState: string | null | undefined): ReportDraftState | null {
	return draftState != null && KNOWN_DRAFT_STATES.has(draftState) ? (draftState as ReportDraftState) : null;
}

/** A lifecycle mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type LifecycleMutationModule = {commit: unknown};

/** Runs a lifecycle mutation as a promise. Same reasoning as `commitSave`: the surface reports a
 * failed lifecycle action itself and keeps the editor where they are, so the default global
 * mutation-error modal is deliberately not delegated to. A response without a `draftState` and
 * without `locked` is treated as a failure, because the surface would otherwise show a state it
 * never received. */
function commitLifecycle(
	mutation: LifecycleMutationModule,
	reportId: string,
	rootField: string,
	lockToken?: string | null
): Promise<ReportLifecycleState> {
	return new Promise<ReportLifecycleState>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			// The token is omitted rather than sent null when this tab holds none: report-service reads a
			// presented token as "this caller participates in the lock protocol" and refuses the write when
			// it no longer names a live lock the caller holds.
			lockToken ? {reportId, lockToken} : {reportId},
			(response: LifecycleMutationResponse) => {
				const payload = response?.[rootField];
				if (!payload || (payload.draftState == null && !payload.locked)) {
					reject(new Error(`${rootField} returned no lifecycle state`));
					return;
				}
				resolve({
					draftState: narrowDraftState(payload.draftState),
					publishedAt: payload.publishedAt ?? null,
					locked: payload.locked === true,
					draftFilterWillRevert: payload.draftFilterWillRevert === true,
				});
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** The lock holder/claim shape every lock query and mutation returns a subset of. Absent fields
 * (a heartbeat carries no `holder`/`lockedAt`) are read as null rather than assumed present. */
type LockPayload = {
	token?: string | null;
	locked?: boolean | null;
	heldByViewer?: boolean | null;
	holder?: {id: string; fullName: string; profilePictureId: string | null} | null;
	lockedAt?: string | null;
	expiresAt?: string | null;
};

function toLockStatus(payload: LockPayload | null | undefined): LockStatus {
	return {
		locked: payload?.locked === true,
		// Deliberately not coerced to a boolean: null means the server could not resolve whether the
		// viewer is the holder, and reading that as "not the viewer" is what made a user-service blip
		// look like a takeover to the tab that actually held the lock. Absent (a payload that never
		// selects the field) is the same unknown.
		heldByViewer: payload?.heldByViewer ?? null,
		holder: payload?.holder ?? null,
		lockedAt: payload?.lockedAt ?? null,
		expiresAt: payload?.expiresAt ?? null,
	};
}

function toLockClaim(payload: LockPayload | null | undefined): LockClaim {
	return {token: payload?.token ?? null, status: toLockStatus(payload)};
}

/** A lock mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type LockMutationModule = {commit: unknown};

/** Runs a lock-claiming mutation (acquire, heartbeat) as a promise that RESOLVES,
 * never rejects, on a lost race: report-service's 409 is already mapped by `ReportServiceLogic` to a
 * normal (non-error) payload with `token: null`, so a contended lock is read the same way here — only
 * a transport failure reaches `onError`. */
function commitLockClaim(mutation: LockMutationModule, input: Record<string, unknown>, rootField: string): Promise<LockClaim> {
	return new Promise<LockClaim>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			input,
			(response: Record<string, LockPayload | undefined>) => resolve(toLockClaim(response?.[rootField])),
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** A favorite-toggle mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type ToggleFavoriteMutationModule = {commit: unknown};

type ToggleFavoriteMutationResponse = {
	updateTask?: {taskEdge?: {node?: {id?: string; favoured?: boolean}}};
};

/** Runs the favorite-toggle mutation as a promise. Unlike `commitSave`/`commitLifecycle`, the caller
 * (`ReportBuilderContext`) has already flipped the row optimistically and only needs to know whether
 * to revert it — there is no lifecycle state or saved-report reference to extract on success. */
function commitToggleFavorite(mutation: ToggleFavoriteMutationModule, taskId: string, favoured: boolean): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			{ids: [toRelayGlobalId('Task', taskId)], favoured},
			(response: ToggleFavoriteMutationResponse) => {
				const node = response?.updateTask?.taskEdge?.node;
				if (!node) {
					reject(new Error('updateTask returned no task'));
					return;
				}
				// service-company can refuse the update (a locked task, a restricted project, a task
				// deleted between the fetch and the click) without the transport-level failure the
				// mutation otherwise catches: the graphql server re-fetches and returns the task
				// UNCHANGED instead of rethrowing, so `node` is present but `favoured` never actually
				// changed. Comparing against what was requested catches that silent refusal so the
				// caller still rolls back its optimistic flip.
				if (node.favoured !== favoured) {
					reject(new Error('updateTask did not apply the requested favoured state'));
					return;
				}
				resolve();
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

export class HttpReportDataSource implements ReportDataSource {
	async fetchRows(
		definition: ReportDefinitionV1,
		page: {offset: number; limit: number},
		taxonomy?: ReportTaxonomy | null,
		formatLabel?: FormatLabel,
		grouping?: GroupingToken[]
	): Promise<ReportDataResult> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(FETCH_PATH), {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify(buildFetchBody(definition, page, grouping ?? [], taxonomy)),
		});
		if (!response.ok) {
			// Carries the status as a field, not just in the message: the surface words an access refusal
			// differently and hides the retry, which it cannot decide from a prose message.
			throw new ReportDataFetchError(response.status, `report/data/fetch failed: ${response.status}`);
		}
		const nodes = (await response.json()) as FieldNode[];
		return mapFetchResponse(
			Array.isArray(nodes) ? nodes : [],
			page.limit,
			fetchMappingContext(definition, grouping ?? [], taxonomy, formatLabel)
		);
	}

	async loadDefinition(savedReportId: string): Promise<LoadedReport> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(`${DEFINITION_PATH}/${savedReportId}`), {
			method: 'GET',
			credentials: 'include',
		});
		if (!response.ok) {
			// Same typed error as the row fetch. This is the first request on the saved-report path, so a
			// refusal here is the one a viewer opening someone else's report actually hits — losing the
			// status would leave that case with nothing but a generic "could not be opened".
			//
			// A 403 also carries which refusal it was, so the page can say who to ask. The body is read
			// defensively: one that is not JSON, or names no known reason, leaves the reason unset rather
			// than turning a refusal into a parse error.
			let reason: ReportRefusalReason | undefined;
			if (response.status === 403) {
				const body = (await response.json().catch(() => null)) as {reason?: unknown} | null;
				const named = typeof body === 'object' && body !== null ? body.reason : undefined;
				reason = named === 'REPORT_TYPE_PERMISSION' || named === 'NOT_SHARED' ? named : undefined;
			}
			throw new ReportDataFetchError(response.status, `report/definition failed: ${response.status}`, reason);
		}
		const stored = (await response.json()) as Record<string, unknown>;
		// Fails closed: an unreadable or absent permission hides edit affordances rather than offering
		// edits the backend will reject.
		const viewerPermission: ViewerPermission =
			stored.viewerPermission === 'OWNER' || stored.viewerPermission === 'EDITOR' ? stored.viewerPermission : 'VIEWER';
		return {
			definition: toReportDefinition(stored),
			// An unreadable or absent origin is treated as a standard report: the surface must not claim
			// an AI origin it cannot prove.
			source: stored.source === 'AI' ? 'AI' : 'STANDARD',
			promptText: typeof stored.promptText === 'string' && stored.promptText !== '' ? stored.promptText : null,
			// Strict `=== true`: anything else means the flag is not proven, and the banner must not claim
			// an edit it cannot back up.
			manuallyEdited: stored.manuallyEdited === true,
			viewerPermission,
			// Same narrowing the lifecycle mutations use: the stored state is a free-form string on the wire,
			// so anything outside the known union becomes null rather than reaching the union type unchecked.
			draftState: narrowDraftState(typeof stored.draftState === 'string' ? stored.draftState : null),
			publishedAt: typeof stored.publishedAt === 'string' && stored.publishedAt !== '' ? stored.publishedAt : null,
			// Deliberately not defaulted: a non-boolean means the stored value is unknown, and an update
			// overwrites this field unconditionally, so guessing would flip the report between private and
			// shared.
			privateAccess: typeof stored.privateAccess === 'boolean' ? stored.privateAccess : null,
			draftFilterWillRevert: stored.draftFilterWillRevert === true,
		};
	}

	saveDefinition(
		definition: ReportDefinitionV1,
		saved: SavedReportRef | null,
		promptText: string | null,
		manuallyEdited: boolean,
		lockToken?: string | null
	): Promise<SavedReportRef> {
		return saved === null
			? // A create has no report to lock yet, so there is no token to present and nothing to gate.
			  commitSave(
					SaveReportAsMutation,
					buildSaveReportAsInput(definition, promptText, manuallyEdited),
					response => response?.saveReportAs?.savedReport
			  )
			: commitSave(
					UpdateSavedReportMutation,
					buildUpdateSavedReportInput(saved, definition, manuallyEdited, lockToken),
					response => response?.updateSavedReport?.savedReport
			  );
	}

	async exportCsv(
		definition: ReportDefinitionV1,
		grouping: GroupingToken[],
		hiddenColumnPaths?: readonly string[],
		taxonomy?: ReportTaxonomy | null
	): Promise<void> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(EXPORT_PATH), {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify(buildExportBody(definition, grouping, hiddenColumnPaths, taxonomy)),
		});
		if (!response.ok) {
			// A 413 is the one failure retrying cannot fix: report-service refuses an ordered export whose
			// result set exceeds its row ceiling, and the same request will exceed it again. Raised as its own
			// error type so the surface can say to narrow the report instead of telling the user to try again.
			if (response.status === EXPORT_TOO_LARGE_STATUS) {
				throw new ReportExportTooLargeError();
			}
			throw new ReportDataFetchError(response.status, `report/data/export failed: ${response.status}`);
		}
		const blob = await response.blob();
		Util.exportToCSV(blob, definition.name);
	}

	enterDraft(savedReportId: string): Promise<ReportLifecycleState> {
		return commitLifecycle(EnterReportDraftMutation, savedReportId, 'enterReportDraft');
	}

	publish(savedReportId: string, lockToken?: string | null): Promise<ReportLifecycleState> {
		return commitLifecycle(PublishReportMutation, savedReportId, 'publishReport', lockToken);
	}

	discard(savedReportId: string, lockToken?: string | null): Promise<ReportLifecycleState> {
		return commitLifecycle(DiscardReportDraftMutation, savedReportId, 'discardReportDraft', lockToken);
	}

	lockStatus(savedReportId: string): Promise<LockStatus> {
		return fetchQuery(environment.getInstance(), ReportLockStatusQuery, {reportId: savedReportId})
			.toPromise()
			.then((data: {viewer?: {reportLockStatus?: LockPayload | null} | null} | null | undefined) =>
				toLockStatus(data?.viewer?.reportLockStatus)
			);
	}

	acquireLock(savedReportId: string): Promise<LockClaim> {
		return commitLockClaim(AcquireReportLockMutation, {reportId: savedReportId}, 'acquireReportLock');
	}

	heartbeatLock(savedReportId: string, token: string): Promise<LockClaim> {
		return commitLockClaim(HeartbeatReportLockMutation, {reportId: savedReportId, token}, 'heartbeatReportLock');
	}

	releaseLock(savedReportId: string, token: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			Util.CommitMutation(
				ReleaseReportLockMutation,
				{reportId: savedReportId, token},
				() => resolve(),
				true,
				null,
				(transaction: unknown) => reject(new Error(String(transaction)))
			);
		});
	}

	async toggleFavorite(taskId: string, favoured: boolean): Promise<void> {
		return commitToggleFavorite(FavoriteTaskMutation, taskId, favoured);
	}
}
